import clsx from 'clsx';
import { ReactNode } from 'react';

interface AmbientCardProps {
  active: boolean;
  onClick: () => void;
  label: string;
  description: string;
  /** Gradiente CSS de fondo (sustituible por foto real más adelante) */
  gradient: string;
  /** Glyph SVG decorativo (arabesco, sol, etc.) — se centra arriba */
  glyph?: ReactNode;
  /** Si el grupo ya tiene alguna selección, atenúa el resto */
  groupHasSelection?: boolean;
}

export default function AmbientCard({
  active,
  onClick,
  label,
  description,
  gradient,
  glyph,
  groupHasSelection,
}: AmbientCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'relative overflow-hidden rounded-xl2 transition-all duration-200',
        'min-h-[220px] flex flex-col items-center justify-end text-center',
        'p-5 focus:outline-none',
        active
          ? 'ring-2 ring-elixir-gold ring-offset-2 ring-offset-elixir-black shadow-[0_8px_24px_-10px_rgba(201,169,97,0.45)]'
          : groupHasSelection
          ? 'opacity-55 hover:opacity-95 ring-1 ring-elixir-gold/15'
          : 'ring-1 ring-elixir-gold/15 hover:ring-elixir-gold/45'
      )}
      style={{ background: gradient }}
    >
      {/* Veil de legibilidad */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/15 pointer-events-none" />

      {/* Glyph centrado arriba */}
      {glyph && (
        <div className="relative w-full flex justify-center mb-auto pt-2 text-white/35">
          <div className="w-12 h-12">{glyph}</div>
        </div>
      )}

      {/* Check badge cuando está activo */}
      {active && (
        <span className="absolute -top-2.5 -right-2.5 w-7 h-7 rounded-full bg-elixir-gold text-elixir-black flex items-center justify-center shadow ring-2 ring-elixir-black z-20">
          <svg width="14" height="14" viewBox="0 0 18 18" fill="none">
            <path
              d="M3.5 9.5 L7 13 L14.5 5"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      )}

      {/* Texto abajo */}
      <div className="relative w-full">
        <h4 className="serif italic text-white text-2xl leading-tight">
          {label}
        </h4>
        <p className="text-[11px] uppercase tracking-[0.18em] text-white/75 mt-2">
          {description}
        </p>
      </div>
    </button>
  );
}

/** Glyph helper components — arabescos SVG inline, mejor que emojis */
export const Glyphs = {
  Sun: (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <circle cx="24" cy="24" r="6" />
      {Array.from({ length: 12 }).map((_, i) => {
        const a = (i * Math.PI) / 6;
        const x1 = 24 + Math.cos(a) * 11;
        const y1 = 24 + Math.sin(a) * 11;
        const x2 = 24 + Math.cos(a) * 18;
        const y2 = 24 + Math.sin(a) * 18;
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
      })}
    </svg>
  ),
  Flower: (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5">
      {Array.from({ length: 8 }).map((_, i) => {
        const a = (i * Math.PI) / 4;
        const cx = 24 + Math.cos(a) * 9;
        const cy = 24 + Math.sin(a) * 9;
        return <ellipse key={i} cx={cx} cy={cy} rx="5" ry="9" transform={`rotate(${(i * 45) + 90} ${cx} ${cy})`} />;
      })}
      <circle cx="24" cy="24" r="3" fill="currentColor" />
    </svg>
  ),
  Star: (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5">
      {/* Estrella de 8 puntas (arabesca) */}
      <path d="M24 4 L29 19 L44 24 L29 29 L24 44 L19 29 L4 24 L19 19 Z" />
      <path d="M10 10 L24 14 L38 10 L34 24 L38 38 L24 34 L10 38 L14 24 Z" />
    </svg>
  ),
  Leaf: (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <path d="M24 6 C12 12, 8 24, 12 38 C18 36, 32 30, 38 14 C32 14, 28 16, 24 22" />
      <path d="M24 22 L20 30" />
      <path d="M24 22 L28 28" />
    </svg>
  ),
};
